import React from 'react';
import { FormattedMessage, InjectedIntlProps, injectIntl } from 'react-intl';
import { Well, Button, ButtonToolbar, Collapse, Label, Modal, MenuItem, SplitButton } from 'react-bootstrap';
import { Link } from 'react-router';
import urlMapping from '../app/URLMapping';
import AppPanel from '../fw/AppPanel.react';
import types from '../../common/model-api/types';
import DiffViewer from './DiffViewer';
import StepDiff, { stringifyDiffValue } from './StepDiff';
import {
  buildSnapshotValidationMarkdown,
  getDiffDisplayStatus,
  redactSnapshotExportValue,
} from './markdownExport';
import './FlowTestResultView.scss';

// Interfaces for data structures
interface TestRun {
  id: string;
  realId?: string;
  status: string;
  createdAt: string;
  actualOutput: any;
  computedDiff?: any;
  error?: string;
  duration?: number;
  expiresIn?: number;
}

interface FlowStepDef {
  id: string;
  realId: string;
  name: any;
  type: string;
}

interface TestInvocation {
  id: string;
  realId: string;
  name: string;
  organisation_id: string;
  flow_id: string;
  flowDef_id?: string;
  flowDefVersion_id?: string;
  sourceFlowInvocation_id?: string;
  expectedOutput: any;
  version?: number;
  createdAt?: string;
  updatedAt?: string;
  lastStatus?: string;
  lastRunAt?: string;
  lastDuration?: number;
  lastError?: string;
  environmentState?: any;
  fetchIds?: any;
  startType?: string;
  originalRequest?: any;
  originalHeaders?: any;
  originalUrl?: string;
  originalRequestUrl?: string;
  flowDef?: {
    flowStepDefs?: {
      edges: Array<{ node: FlowStepDef }>;
    };
  };
  runs?: {
    edges: Array<{ node: TestRun }>;
  };
}

interface Group {
  stepId: string;
  stepName: any;
  stepType: string;
  writes: any[];
  hasMismatch: boolean;
}

interface FlowTestResultViewProps {
  intl: InjectedIntlProps['intl'];
  params: {
    organisationId: string;
    flowId: string;
  };
  testInvocation: TestInvocation;
  lastRun: TestRun | null;
  groups: Group[];
  expectedSummary: any;
  actualSummary: any;
  hasSummaryMismatch: boolean;
  isDiffUnavailable?: boolean;
  mismatchCount: number;
  maxWritesLen: number;
  expandedGroupIndices: Set<number>;
  diffsCommand?: { action: string; id: number } | null;
  conflictError: string | null;
  onAccept: () => void;
  onManualUpdate: (expectedOutput: any) => void;
  onEditEnvironmentState: (environmentStateJson: string) => void;
  onDelete: () => void;
  onGroupToggle: (index: number) => void;
  onExpandAllGroups: () => void;
  onCollapseAllGroups: () => void;
  onExpandAllDiffs: () => void;
  onCollapseAllDiffs: () => void;
  isHistoricalRun?: boolean;
}

interface FlowTestResultViewState {
  showManualUpdateModal: boolean;
  manualUpdateJson: string;
  jsonError: string | null;
  showEditSnapshotModal: boolean;
  editSnapshotJson: string;
  editSnapshotJsonError: string | null;
}

export function buildBrowserDebugExportPayload(props: FlowTestResultViewProps) {
  const { testInvocation, lastRun, groups, expectedSummary, actualSummary, hasSummaryMismatch, mismatchCount, maxWritesLen } = props;
  const markdownDiff = buildSnapshotValidationMarkdown(props, { includeOnlyDiffs: true });
  const redactedTestInvocation = redactSnapshotExportValue(testInvocation);
  const redactedLastRun = lastRun ? redactSnapshotExportValue(lastRun) : null;

  return {
    exportedAt: new Date().toISOString(),
    markdownDiff,
    testInvocation: {
      id: redactedTestInvocation.id,
      realId: redactedTestInvocation.realId,
      name: redactedTestInvocation.name,
      organisation_id: redactedTestInvocation.organisation_id,
      flow_id: redactedTestInvocation.flow_id,
      flowDef_id: redactedTestInvocation.flowDef_id,
      flowDefVersion_id: redactedTestInvocation.flowDefVersion_id,
      sourceFlowInvocation_id: redactedTestInvocation.sourceFlowInvocation_id,
      version: redactedTestInvocation.version,
      createdAt: redactedTestInvocation.createdAt,
      updatedAt: redactedTestInvocation.updatedAt,
      lastStatus: redactedTestInvocation.lastStatus,
      lastRunAt: redactedTestInvocation.lastRunAt,
      lastDuration: redactedTestInvocation.lastDuration,
      lastError: redactedTestInvocation.lastError,
      startType: redactedTestInvocation.startType,
    },
    testRun: redactedLastRun ? {
      id: redactedLastRun.id,
      realId: redactedLastRun.realId,
      status: redactedLastRun.status,
      createdAt: redactedLastRun.createdAt,
      error: redactedLastRun.error,
      duration: redactedLastRun.duration,
      expiresIn: redactedLastRun.expiresIn,
    } : null,
    runStatus: redactedLastRun?.status ?? null,
    expectedSummary: redactSnapshotExportValue(expectedSummary),
    actualSummary: redactSnapshotExportValue(actualSummary),
    hasSummaryMismatch,
    mismatchCount,
    maxWritesLen,
    groups: redactSnapshotExportValue(groups),
  };
}

class FlowTestResultView extends React.Component<FlowTestResultViewProps, FlowTestResultViewState> {
  constructor(props: FlowTestResultViewProps) {
    super(props);
    this.state = {
      showManualUpdateModal: false,
      manualUpdateJson: '',
      jsonError: null,
      showEditSnapshotModal: false,
      editSnapshotJson: '',
      editSnapshotJsonError: null,
    };
  }

  handleOpenManualUpdate = () => {
    const { testInvocation, lastRun } = this.props;
    const currentOutput = lastRun?.actualOutput || testInvocation.expectedOutput || {};
    this.setState({
      showManualUpdateModal: true,
      manualUpdateJson: JSON.stringify(currentOutput, null, 2),
      jsonError: null,
    });
  };

  handleCloseManualUpdate = () => {
    this.setState({ showManualUpdateModal: false, jsonError: null });
  };

  handleManualUpdateChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    this.setState({ manualUpdateJson: e.target.value, jsonError: null });
  };

  handleSaveManualUpdate = () => {
    const { manualUpdateJson } = this.state;
    try {
      const parsed = JSON.parse(manualUpdateJson);
      this.props.onManualUpdate(parsed);
      this.handleCloseManualUpdate();
    } catch (e: any) {
      this.setState({ jsonError: e.message });
    }
  };

  handleExportDebuggingJson = () => {
    const { testInvocation } = this.props;
    const payload = buildBrowserDebugExportPayload(this.props);
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `debugging-export-${testInvocation.name || 'report'}-${Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  handleOpenEditSnapshot = () => {
    const { testInvocation } = this.props;
    const es = testInvocation.environmentState;
    const json = es ? JSON.stringify(typeof es === 'string' ? JSON.parse(es) : es, null, 2) : '{}';
    this.setState({ showEditSnapshotModal: true, editSnapshotJson: json, editSnapshotJsonError: null });
  };

  handleCloseEditSnapshot = () => {
    this.setState({ showEditSnapshotModal: false, editSnapshotJsonError: null });
  };

  handleEditSnapshotChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    this.setState({ editSnapshotJson: e.target.value, editSnapshotJsonError: null });
  };

  handleSaveEditSnapshot = () => {
    const { editSnapshotJson } = this.state;
    try {
      JSON.parse(editSnapshotJson); // validate
      this.props.onEditEnvironmentState(editSnapshotJson);
      this.handleCloseEditSnapshot();
    } catch (e: any) {
      this.setState({ editSnapshotJsonError: e.message });
    }
  };

  handleExportMarkdown = (includeOnlyDiffs: boolean = true) => {
    const { testInvocation } = this.props;
    const markdownContent = buildSnapshotValidationMarkdown(this.props, { includeOnlyDiffs });
    const blob = new Blob([markdownContent], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `snapshot-diff-${testInvocation.name || 'report'}.md`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  render() {
    const {
      intl,
      params,
      testInvocation,
      lastRun,
      groups,
      expectedSummary,
      actualSummary,
      hasSummaryMismatch,
      isDiffUnavailable,
      mismatchCount,
      maxWritesLen,
      expandedGroupIndices,
      diffsCommand,
      onAccept,
      onDelete,
      onGroupToggle,
      onExpandAllGroups,
      onCollapseAllGroups,
      onExpandAllDiffs,
      onCollapseAllDiffs,
      isHistoricalRun,
    } = this.props;

    const { showManualUpdateModal, manualUpdateJson, jsonError } = this.state;
    const { showEditSnapshotModal, editSnapshotJson, editSnapshotJsonError } = this.state;

    const { flowId, organisationId } = params;

    const breadcrumbParams = [
      { type: types['organisations'], data: { realId: organisationId, id: organisationId, name: 'Organisation' } },
      { type: types['flows'], data: { realId: flowId, id: flowId, name: 'Flow' } },
      { type: { entity: () => 'snapshotTesting', getDefaultTitle: () => 'Validate' }, data: { name: 'Validate' } }
    ];

    return (
      <AppPanel
        breadcrumbParams={breadcrumbParams}
        title={intl.formatMessage({ id: 'snapshotTesting.validateTitle', defaultMessage: 'Validate Snapshot' })}
      >
        <div className="snapshot-validation-container">
          <div className="header-section">
            <div className="meta-info">
              <h3>
                <i className="fa fa-check-square-o" /> <FormattedMessage defaultMessage="Validate Snapshot" id="snapshotTesting.validateHeader" />
              </h3>
              <Well bsSize="small">
                <strong><FormattedMessage defaultMessage="Test:" id="snapshotTesting.metaTest" /></strong> {testInvocation.name}<br />
                <strong><FormattedMessage defaultMessage="Run At:" id="snapshotTesting.metaRunAt" /></strong> {lastRun ? lastRun.createdAt : <FormattedMessage defaultMessage="N/A" id="common.notAvailableShort" />}<br />
                <strong><FormattedMessage defaultMessage="Status:" id="snapshotTesting.metaStatus" /></strong> {lastRun ? lastRun.status : <FormattedMessage defaultMessage="Not run" id="snapshotTesting.notRun" />}<br />
                {testInvocation.sourceFlowInvocation_id && (
                  <span>
                    <strong><FormattedMessage defaultMessage="Original Invocation:" id="snapshotTesting.metaOriginalInvocation" /></strong>{' '}
                    <Link to={urlMapping.getUrl('flowInvocations', 'view', {
                      organisation_id: testInvocation.organisation_id,
                      flow_id: testInvocation.flow_id,
                      id: testInvocation.sourceFlowInvocation_id
                    })}>
                      <FormattedMessage defaultMessage="view invocation" id="snapshotTesting.viewInvocationLink" />
                    </Link>
                  </span>
                )}
              </Well>
            </div>
            <div className="actions">
              <ButtonToolbar>
                <Button
                  bsStyle="success"
                  disabled={!lastRun || isHistoricalRun}
                  onClick={onAccept}
                  title={isHistoricalRun ? intl.formatMessage({ id: 'snapshotTesting.historicalRunDisabled', defaultMessage: 'Actions are disabled when viewing historical runs.' }) : undefined}
                >
                  <i className="fa fa-check" /> <FormattedMessage defaultMessage="Accept & Update" id="snapshotTesting.accept" />
                </Button>
                <Button
                  bsStyle="primary"
                  disabled={isHistoricalRun}
                  onClick={this.handleOpenManualUpdate}
                  title={isHistoricalRun ? intl.formatMessage({ id: 'snapshotTesting.historicalRunDisabled', defaultMessage: 'Actions are disabled when viewing historical runs.' }) : undefined}
                >
                  <i className="fa fa-edit" /> <FormattedMessage defaultMessage="Manual Update" id="snapshotTesting.manualUpdate" />
                </Button>
                <Button
                  bsStyle="warning"
                  disabled={isHistoricalRun || !testInvocation.environmentState}
                  onClick={this.handleOpenEditSnapshot}
                  title={isHistoricalRun ? intl.formatMessage({ id: 'snapshotTesting.historicalRunDisabled', defaultMessage: 'Actions are disabled when viewing historical runs.' }) : undefined}
                >
                  <i className="fa fa-sliders" /> <FormattedMessage defaultMessage="Edit Snapshot" id="snapshotTesting.editSnapshot" />
                </Button>
                <SplitButton
                  bsStyle="default"
                  id="snapshotTesting-exportMarkdown"
                  title={<span><i className="fa fa-download" /> <FormattedMessage defaultMessage="Export Markdown" id="snapshotTesting.exportMarkdown" /></span>}
                  onClick={() => this.handleExportMarkdown()}
                >
                  <MenuItem onClick={() => this.handleExportMarkdown(true)}>
                    <FormattedMessage defaultMessage="Include only diffs" id="snapshotTesting.exportMarkdownDiffsOnly" />
                  </MenuItem>
                  <MenuItem onClick={this.handleExportDebuggingJson}>
                    <FormattedMessage defaultMessage="Debugging export" id="snapshotTesting.exportDebuggingJson" />
                  </MenuItem>
                </SplitButton>
                <Button bsStyle="danger" onClick={onDelete}>
                  <i className="fa fa-trash" /> <FormattedMessage defaultMessage="Delete" id="snapshotTesting.delete" />
                </Button>
              </ButtonToolbar>
              {this.props.conflictError && (
                <Well bsSize="small" className="conflict-error" style={{ marginTop: '10px', backgroundColor: '#fcf8e3', borderColor: '#faebcc' }}>
                  <i className="fa fa-exclamation-triangle" style={{ color: '#8a6d3b', marginRight: '8px' }} />
                  <span style={{ color: '#8a6d3b' }}>{this.props.conflictError}</span>
                </Well>
              )}
            </div>
          </div>

          <div className="diff-section">
            <div className="section-header">
              <h4><FormattedMessage defaultMessage="Diff" id="snapshotTesting.diff" /></h4>
            </div>

            {hasSummaryMismatch && (
              <div className="summary-mismatch">
                <div className="summary-headers">
                  <h4><FormattedMessage defaultMessage="Expected Summary" id="snapshotTesting.expectedSummary" /></h4>
                  <h4><FormattedMessage defaultMessage="Actual Summary" id="snapshotTesting.actualSummary" /></h4>
                </div>
                <DiffViewer
                  actual={stringifyDiffValue(actualSummary)}
                  expected={stringifyDiffValue(expectedSummary)}
                  filename="summary.json"
                />
              </div>
            )}

            <div className="timeline-view">
              <div className="timeline-header">
                <h4>
                  <FormattedMessage defaultMessage="Step Timeline" id="snapshotTesting.timeline" />
                  {maxWritesLen > 0 && (
                    <small>
                      (
                      <FormattedMessage
                        defaultMessage="{count, plural, one {# write} other {# writes}}"
                        id="snapshotTesting.writeCount"
                        values={{ count: maxWritesLen }}
                      />
                      {mismatchCount > 0 && (
                        <span>
                          , <FormattedMessage
                            defaultMessage="{count} mismatched"
                            id="snapshotTesting.mismatchCount"
                            values={{ count: mismatchCount }}
                          />
                        </span>
                      )}
                      )
                    </small>
                  )}
                </h4>
                <div className="btn-group btn-group-xs">
                  <Button onClick={onExpandAllGroups}>
                    <FormattedMessage defaultMessage="Expand Steps" id="snapshotTesting.expandSteps" />
                  </Button>
                  <Button onClick={onCollapseAllGroups}>
                    <FormattedMessage defaultMessage="Collapse Steps" id="snapshotTesting.collapseSteps" />
                  </Button>
                  <Button onClick={onExpandAllDiffs}>
                    <FormattedMessage defaultMessage="Expand Diffs" id="snapshotTesting.expandDiffs" />
                  </Button>
                  <Button onClick={onCollapseAllDiffs}>
                    <FormattedMessage defaultMessage="Collapse Diffs" id="snapshotTesting.collapseDiffs" />
                  </Button>
                </div>
              </div>

              {isDiffUnavailable ? (
                <div className="no-writes">
                  <FormattedMessage defaultMessage="Diff unavailable for this run." id="snapshotTesting.diffUnavailable" />
                </div>
              ) : groups.length > 0 ? (
                <div className="panel-group" id="snapshot-accordion">
                  {groups.map((group, idx) => {
                    const isOpen = expandedGroupIndices.has(idx);
                    const groupStatus = getDiffDisplayStatus(group.hasMismatch, lastRun ? lastRun.status : undefined);
                    return (
                      <div className={`panel panel-${group.hasMismatch ? 'danger' : 'default'}`} key={idx}>
                        <button
                          aria-controls={`snapshot-testing-validation-group-${idx}`}
                          aria-expanded={isOpen}
                          className="panel-heading"
                          onClick={() => onGroupToggle(idx)}
                          type="button"
                        >
                          <span className="panel-title-content">
                            <span>
                              <strong>{group.stepName}</strong>
                              {group.stepType && <span className="text-muted"> ({group.stepType})</span>}
                            </span>
                            <Label bsStyle={groupStatus.bsStyle}>
                              {groupStatus.label}
                            </Label>
                          </span>
                        </button>
                        <Collapse in={isOpen}>
                          <div className="panel-body" id={`snapshot-testing-validation-group-${idx}`}>
				{group.writes.map((write, wIdx) => (
                  <StepDiff
                    actionName={write.actionName}
                    fetchName={write.fetchName}
                    expected={typeof write.expectedPreview !== 'undefined' ? write.expectedPreview : write.expected}
                    actual={typeof write.actualPreview !== 'undefined' ? write.actualPreview : write.actual}
                    index={write.index}
                    isMismatched={write.isMismatched}
                    status={getDiffDisplayStatus(write.isMismatched, lastRun ? lastRun.status : undefined)}
                    diffsCommand={diffsCommand || undefined}
                    key={wIdx}
                    stepId={write.stepId}
                    stepName={write.stepName}
                    stepType={write.stepType}
                  />
                ))}
                          </div>
                        </Collapse>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="no-writes">
                  <FormattedMessage defaultMessage="No writes recorded." id="snapshotTesting.noWrites" />
                </div>
              )}
            </div>
          </div>
        </div>

        <Modal show={showManualUpdateModal} onHide={this.handleCloseManualUpdate}>          <Modal.Header closeButton>
            <Modal.Title>
              <FormattedMessage defaultMessage="Edit Expected Output" id="snapshotTesting.editExpectedOutput" />
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              <FormattedMessage
                defaultMessage="Edit the JSON below to set the expected output. Changes will be normalized before saving."
                id="snapshotTesting.manualUpdateHelp"
              />
            </p>
            <textarea
              className="form-control"
              rows={20}
              style={{ fontFamily: 'monospace', fontSize: '12px' }}
              value={manualUpdateJson}
              onChange={this.handleManualUpdateChange}
            />
            {jsonError && (
              <div className="alert alert-danger" style={{ marginTop: '10px' }}>
                <strong><FormattedMessage defaultMessage="JSON Error:" id="snapshotTesting.jsonErrorLabel" /></strong> {jsonError}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button onClick={this.handleCloseManualUpdate}>
              <FormattedMessage defaultMessage="Cancel" id="common.cancel" />
            </Button>
            <Button bsStyle="primary" onClick={this.handleSaveManualUpdate}>
              <i className="fa fa-save" /> <FormattedMessage defaultMessage="Save" id="common.save" />
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={showEditSnapshotModal} onHide={this.handleCloseEditSnapshot} bsSize="large">
          <Modal.Header closeButton>
            <Modal.Title>
              <FormattedMessage defaultMessage="Edit Snapshot (environmentState)" id="snapshotTesting.editSnapshotTitle" />
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p style={{ color: '#8a6d3b' }}>
              <i className="fa fa-exclamation-triangle" />{' '}
              <FormattedMessage
                defaultMessage="Edit the snapshot's environmentState JSON directly. Useful for adjusting timestamps (e.g. flowInvocationCreatedAt) to fix time-sensitive replays."
                id="snapshotTesting.editSnapshotHelp"
              />
            </p>
            <textarea
              className="form-control"
              rows={28}
              style={{ fontFamily: 'monospace', fontSize: '12px' }}
              value={editSnapshotJson}
              onChange={this.handleEditSnapshotChange}
            />
            {editSnapshotJsonError && (
              <div className="alert alert-danger" style={{ marginTop: '10px' }}>
                <strong><FormattedMessage defaultMessage="JSON Error:" id="snapshotTesting.jsonErrorLabel" /></strong> {editSnapshotJsonError}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button onClick={this.handleCloseEditSnapshot}>
              <FormattedMessage defaultMessage="Cancel" id="common.cancel" />
            </Button>
            <Button bsStyle="warning" onClick={this.handleSaveEditSnapshot} disabled={!!editSnapshotJsonError}>
              <i className="fa fa-save" /> <FormattedMessage defaultMessage="Save Snapshot" id="snapshotTesting.saveSnapshot" />
            </Button>
          </Modal.Footer>
        </Modal>
      </AppPanel>
    );
  }
}

export default injectIntl(FlowTestResultView);
